import { useChainIsSupported } from "connectkit";
import type { SubmitHandler } from "react-hook-form";
import { useSwitchChain } from "wagmi";

import { CHAIN } from "../../constants";
import type { PaymentFormValues } from "../PaymentForm";
import { PAYMENT_ERROR, PAYMENT_ERROR_SWITCH_CHAIN } from "../errors";
import { useStore } from "../store";
import useCheckEmail from "./useCheckEmail";
import useHandleErrors from "./useHandleErrors";
import useHandlePayments from "./useHandlePayments";
import useRegisterPayment from "./useRegisterPayment";

export default function useSubmitForm(paymentMethod: "eth" | "kolz", reset: () => void) {
  const { checkEmail } = useCheckEmail();
  const { registerPayment } = useRegisterPayment();
  const { handleError } = useHandleErrors();
  const { handlePaymentETH, handlePaymentKOLZ } = useHandlePayments();
  const { switchChainAsync } = useSwitchChain();
  const isSupportedChainId = useChainIsSupported();
  const { setTransactionId, setIsProcessing, setIsFailedRegistration } = useStore();

  const onSubmit: SubmitHandler<PaymentFormValues> = async (data) => {
    setIsProcessing(true);
    try {
      await checkEmail(data.email);
    } catch (e) {
      handleError(e);
      return;
    }

    try {
      if (!isSupportedChainId) {
        await switchChainAsync({ chainId: CHAIN.id });
      }
    } catch (e) {
      handleError(e, PAYMENT_ERROR_SWITCH_CHAIN);
      return;
    }

    let transactionId: string;

    try {
      const tx = await (paymentMethod === "eth" ? handlePaymentETH() : handlePaymentKOLZ());

      if (!tx) {
        throw new Error(PAYMENT_ERROR);
      }

      transactionId = tx.toString();
    } catch (e) {
      handleError(e);
      return;
    }

    try {
      await registerPayment(data, paymentMethod, transactionId);
    } catch (e) {
      console.error(e);
      setIsFailedRegistration(true, transactionId);
      return;
    }

    setIsProcessing(false);
    setTransactionId(transactionId);
    reset();
  };

  return { onSubmit };
}
